import React, { type ReactElement, useEffect, useState } from 'react';

import classnames from 'classnames';

import { Skeleton } from '../Skeleton';

import styles from './QrCode.module.scss';

type QrCodeProps = {
    /**
     * The source of the QR code. Can be a base64 string or a URL.
     * If not specified, the loading skeleton will be shown
     */
    src?: string;
    /**
     * If `true`, the loading skeleton will be shown
     */
    loading?: boolean;
    /**
     * The width and height of the QR code. If not specified, it will be `160px`
     */
    width?: number | string;
    /**
     * The height of the QR code. If not specified, it will be the same as the `width`
     */
    height?: number | string;
    className?: string;
};

export const QrCode = ({
    src,
    className,
    loading,
    width = 160,
    height: heightProp
}: QrCodeProps): ReactElement => {
    const height = heightProp ?? width;
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(false);
    }, [src, loading]);

    return (
        <div
            className={classnames(styles.container, className)}
            style={{
                width,
                height
            }}
        >
            {(!src || loading || !isLoaded) && <Skeleton className={styles.qrCodeLoading} />}
            {src && !loading && (
                <img
                    onLoad={() => setIsLoaded(true)}
                    src={src}
                    alt="qr code"
                    className={classnames(styles.qrCode, { [styles.qrCodeLoaded]: isLoaded })}
                    width={160}
                    height={160}
                />
            )}
        </div>
    );
};
